import React, { useMemo } from "react";
import { Select, MenuItem, Box } from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useQuery } from "react-query";
import { useChannels } from "../../hooks/useChannels";
import { getChannelIcon } from "../../utils/utils";
import { fetchData } from "../../api/client";

export const INTERNAL_NOTE_CHANNEL_ID = "internal_note";
/** Canal virtual: envio pelo WhatsApp conectado ao perfil (API Fácil), sem usar origem de lead. */
export const PERSONAL_WHATSAPP_CHANNEL_ID = "personal_whatsapp";

const ChannelSelector = ({
  selectedChannel,
  setSelectedChannel,
  integrated = false,
  /** No compositor do Chat em thread WhatsApp pessoal: só o canal virtual de envio pelo perfil. */
  personalWhatsappComposerOnly = false,
}) => {
  const { data: channelsData } = useChannels();
  const { data: meRoot } = useQuery(["me"], () => fetchData("/users/me"), {
    staleTime: 60000,
  });
  const wp = meRoot?.data?.whatsapp_personal;
  const showPersonalWhatsapp =
    !personalWhatsappComposerOnly &&
    Boolean(wp?.enabled) &&
    String(wp?.status || "") === "connected";

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (value === 0 || value === "0") return "";
    return value;
  };

  const channelOptions = useMemo(() => {
    if (personalWhatsappComposerOnly) {
      return [
        {
          value: PERSONAL_WHATSAPP_CHANNEL_ID,
          label: "WhatsApp pessoal",
          icon: <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />,
        },
      ];
    }
    const fromApi = (channelsData?.channels ?? []).map((channel) => ({
      value: channel.id,
      label: channel.nome,
      icon: getChannelIcon(channel.nome, channel.channelType),
    }));
    const personalOpt = showPersonalWhatsapp
      ? [
          {
            value: PERSONAL_WHATSAPP_CHANNEL_ID,
            label: "WhatsApp pessoal",
            icon: <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />,
          },
        ]
      : [];
    return [
      { value: INTERNAL_NOTE_CHANNEL_ID, label: "Anotações internas", icon: <EditNoteIcon fontSize="small" /> },
      ...personalOpt,
      ...fromApi,
    ];
  }, [channelsData, showPersonalWhatsapp, personalWhatsappComposerOnly]);

  const normalizedSelectedChannel = normalizeValue(selectedChannel);
  const valueInOptions = channelOptions.some(
    (opt) =>
      String(opt.value) === String(normalizedSelectedChannel) ||
      (opt.value !== INTERNAL_NOTE_CHANNEL_ID &&
        opt.value !== PERSONAL_WHATSAPP_CHANNEL_ID &&
        parseInt(opt.value) === parseInt(normalizedSelectedChannel))
  );
  const safeSelectValue = channelOptions?.length && valueInOptions
    ? normalizedSelectedChannel
    : (channelOptions?.[0]?.value ?? "");

  return (
    <Select
      value={safeSelectValue}
      onChange={(e) => setSelectedChannel(e.target.value)}
      size="small"
      MenuProps={{
        disableScrollLock: true,
        disablePortal: false,
        container: typeof document !== "undefined" ? document.body : undefined,
        sx: { zIndex: 200001 },
        PaperProps: { sx: { zIndex: 200002 } },
      }}
      sx={{
        minWidth: integrated ? 40 : "32px",
        width: integrated ? 44 : "40px",
        padding: 0,
        bgcolor: integrated ? "transparent" : undefined,
        borderRadius: integrated ? "10px" : undefined,
        transition: integrated ? "background-color 0.15s ease" : undefined,
        "&:hover": integrated
          ? {
              bgcolor: "action.hover",
            }
          : undefined,
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "& .MuiSelect-select": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: integrated ? "6px 4px !important" : "0 !important",
          margin: 0,
          minHeight: integrated ? 40 : undefined,
        },
        "& .MuiSelect-icon": {
          display: "none",
        },
      }}
      renderValue={() => {
        const selectedOption = channelOptions?.find(
          (opt) =>
            safeSelectValue !== "" &&
            (String(opt.value) === String(safeSelectValue) ||
              (opt.value !== INTERNAL_NOTE_CHANNEL_ID &&
                opt.value !== PERSONAL_WHATSAPP_CHANNEL_ID &&
                parseInt(opt.value) === parseInt(safeSelectValue)))
        );
        return (
          selectedOption?.icon || (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getChannelIcon("default", "default")}
            </Box>
          )
        );
      }}
    >
      {channelOptions?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.icon}
          <Box sx={{ marginLeft: 1 }}>{option.label}</Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default ChannelSelector;
